import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { truncate } from '@/seo/config';
import { profileJsonLd } from '@/seo/jsonld';
import { Button } from '@/components/ui/button';
import { Page, PageContent, PageHeader, SectionTitle, EmptyState } from '@/components/layout';
import PostCard from '@/components/PostCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { MapPin, UserPlus, UserMinus, MessageSquare, Settings, BadgeCheck, Crown, Store } from 'lucide-react';

const BADGE_INFO = {
  newcomer: { name: "Nouveau", icon: "🌱" },
  active: { name: "Actif", icon: "⭐" },
  contributor: { name: "Contributeur", icon: "🏆" },
  legend: { name: "Légende", icon: "👑" },
  helper: { name: "Aidant", icon: "💪" },
  popular: { name: "Populaire", icon: "🔥" },
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user: currentUser, api } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);

  const isOwnProfile = !userId || userId === currentUser?.user_id;
  const targetUserId = isOwnProfile ? currentUser?.user_id : userId;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        
        if (isOwnProfile) {
          setProfileUser(currentUser);
        } else {
          const response = await api.get(`/users/${userId}`);
          setProfileUser(response.data);
        }

        // Fetch user's posts
        if (targetUserId) {
          const postsResponse = await api.get('/posts');
          const userPosts = postsResponse.data.filter(p => p.user_id === targetUserId);
          setPosts(userPosts);
          
          // Check if current user follows this profile
          if (!isOwnProfile && currentUser) {
            const followingRes = await api.get(`/users/${currentUser.user_id}/following`);
            setIsFollowing(followingRes.data.some(u => u.user_id === userId));
          }
        }
      } catch (error) {
        toast.error('Erreur lors du chargement du profil');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser || userId) {
      fetchProfile();
    }
  }, [userId, currentUser, isOwnProfile, targetUserId, api]);

  const handleFollow = async () => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }

    setFollowLoading(true);
    try {
      const response = await api.post(`/users/${userId}/follow`);
      setIsFollowing(response.data.following);
      setProfileUser(prev => ({
        ...prev,
        followers_count: prev.followers_count + (response.data.following ? 1 : -1)
      }));
      toast.success(response.data.following ? 'Vous suivez maintenant cet utilisateur' : 'Vous ne suivez plus cet utilisateur');
    } catch (error) {
      toast.error('Erreur lors de l\'action');
    } finally {
      setFollowLoading(false);
    }
  };

  const fetchFollowers = async () => {
    try {
      const response = await api.get(`/users/${targetUserId}/followers`);
      setFollowers(response.data);
    } catch (error) {
      console.error('Error fetching followers:', error);
    }
  };

  const fetchFollowing = async () => {
    try {
      const response = await api.get(`/users/${targetUserId}/following`);
      setFollowing(response.data);
    } catch (error) {
      console.error('Error fetching following:', error);
    }
  };

  const updatePost = (postId, fields) => setPosts((prev) => prev.map((p) => (p.post_id === postId ? { ...p, ...fields } : p)));

  const UserList = ({ items, empty, onPick }) => (
    <ScrollArea className="max-h-[400px]">
      {items.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">{empty}</p>
      ) : (
        <div className="space-y-1">
          {items.map((u) => (
            <button key={u.user_id} className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted text-left" onClick={() => onPick(u.user_id)}>
              <Avatar className="h-10 w-10"><AvatarImage src={u.picture} /><AvatarFallback>{u.name?.charAt(0)}</AvatarFallback></Avatar>
              <div><p className="font-medium text-sm">{u.name}</p><p className="text-xs text-muted-foreground">{u.commune}</p></div>
            </button>
          ))}
        </div>
      )}
    </ScrollArea>
  );

  if (loading) {
    return (
      <Page nav={!!currentUser}>
        <PageContent>
          <div className="surface overflow-hidden"><div className="skeleton h-32 rounded-none" /><div className="p-6 space-y-3"><div className="skeleton h-6 w-1/2" /><div className="skeleton h-4 w-1/3" /></div></div>
        </PageContent>
      </Page>
    );
  }

  const displayName = profileUser?.is_commerce && profileUser?.commerce_name ? profileUser.commerce_name : profileUser?.name;
  const likesTotal = posts.reduce((acc, p) => acc + p.likes_count, 0);

  return (
    <Page nav={!!currentUser} testId="profile-page">
      {profileUser && (
        <SEO
          title={`${displayName} – ${profileUser.commune} | Bourbon`}
          description={truncate(profileUser.bio || profileUser.commerce_description || `Découvrez le profil de ${profileUser.name} à ${profileUser.commune} sur Bourbon, lo réseau social péi de La Réunion.`)}
          path={isOwnProfile ? '/profile' : `/profile/${profileUser.user_id}`}
          image={profileUser.picture || undefined}
          type="profile"
          noindex={isOwnProfile}
          jsonLd={isOwnProfile ? undefined : profileJsonLd(profileUser)}
        />
      )}
      <PageHeader
        back
        backTo={currentUser ? undefined : '/'}
        title={isOwnProfile ? 'Mon profil' : displayName || 'Profil'}
        subtitle={profileUser?.commune}
        actions={
          <>
            {isOwnProfile && (
              <button onClick={() => navigate('/settings')} className="h-10 w-10 rounded-full inline-flex items-center justify-center hover:bg-muted transition-colors" aria-label="Paramètres" data-testid="profile-settings-btn">
                <Settings className="h-5 w-5" />
              </button>
            )}
            {!isOwnProfile && currentUser && (
              <button onClick={() => navigate(`/messages/${userId}`)} className="h-10 w-10 rounded-full inline-flex items-center justify-center hover:bg-muted transition-colors" aria-label="Message" data-testid="send-message-btn">
                <MessageSquare className="h-5 w-5" />
              </button>
            )}
          </>
        }
      />

      <PageContent>
        <section className="post-card animate-fade-in">
          <div className={`relative h-32 sm:h-40 grain ${profileUser?.is_commerce ? 'lagoon-gradient' : 'volcano-gradient'}`}>
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,.35) 0, transparent 40%), radial-gradient(circle at 80% 20%, rgba(255,255,255,.25) 0, transparent 35%)' }} />
            <div className="absolute bottom-3 right-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">{profileUser?.commune} · La Réunion</div>
          </div>
          <div className="px-5 sm:px-7 pb-6">
            <div className="flex items-end justify-between -mt-12 mb-4">
              <Avatar className="h-24 w-24 ring-4 ring-card shadow-xl">
                <AvatarImage src={profileUser?.picture} />
                <AvatarFallback className="text-3xl font-bold bg-secondary/15 text-secondary">{profileUser?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="pb-1">
                {isOwnProfile ? (
                  <Button variant="outline" className="rounded-xl" onClick={() => navigate('/settings')} data-testid="edit-profile-btn">Modifier le profil</Button>
                ) : currentUser ? (
                  <div className="flex gap-2">
                    <Button className="rounded-xl" variant={isFollowing ? 'outline' : 'default'} onClick={handleFollow} disabled={followLoading} data-testid="follow-btn">
                      {followLoading ? <span className="spinner" /> : isFollowing ? <><UserMinus className="h-4 w-4 mr-2" />Ne plus suivre</> : <><UserPlus className="h-4 w-4 mr-2" />Suivre</>}
                    </Button>
                    <Button variant="outline" className="rounded-xl hidden sm:inline-flex" onClick={() => navigate(`/messages/${userId}`)} data-testid="message-btn">
                      <MessageSquare className="h-4 w-4 mr-2" />Message
                    </Button>
                  </div>
                ) : (
                  <Button className="rounded-xl" onClick={() => navigate('/auth')} data-testid="login-to-follow-btn">Connectez-vous pour suivre</Button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold" data-testid="profile-name">{displayName}</h1>
              {profileUser?.is_verified_commerce && <BadgeCheck className="h-5 w-5 text-secondary" />}
              {profileUser?.is_premium && <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 text-amber-700 dark:text-amber-300 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider"><Crown className="h-3 w-3" /> Premium</span>}
            </div>
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4" /> {profileUser?.commune}{profileUser?.quartier ? ` · ${profileUser.quartier}` : ''}</p>
            {profileUser?.is_commerce && (
              <span className="inline-flex items-center gap-1.5 mt-3 rounded-lg bg-accent/10 text-accent px-2.5 py-1 text-xs font-semibold"><Store className="h-3.5 w-3.5" /> {profileUser.commerce_name || 'Commerce local'}</span>
            )}
            {(profileUser?.bio || profileUser?.commerce_description) && (
              <p className="text-[15px] leading-relaxed text-foreground/85 mt-4" data-testid="profile-bio">{profileUser.bio || profileUser.commerce_description}</p>
            )}
            {profileUser?.badges?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4" data-testid="profile-badges">
                {profileUser.badges.map((badge) => (
                  <span key={badge} className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-3 py-1 text-xs font-medium">
                    <span>{BADGE_INFO[badge]?.icon}</span>{BADGE_INFO[badge]?.name}
                  </span>
                ))}
              </div>
            )}

            <div className="grid grid-cols-4 gap-2 mt-6 rounded-2xl bg-muted/50 p-2">
              <div className="text-center py-2"><div className="font-display text-xl font-bold">{posts.length}</div><div className="text-[11px] text-muted-foreground">Posts</div></div>
              <Dialog open={showFollowers} onOpenChange={setShowFollowers}>
                <DialogTrigger asChild>
                  <button className="text-center py-2 rounded-xl hover:bg-card transition-colors" onClick={fetchFollowers} data-testid="followers-count-btn">
                    <div className="font-display text-xl font-bold">{profileUser?.followers_count || 0}</div><div className="text-[11px] text-muted-foreground">Followers</div>
                  </button>
                </DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Followers</DialogTitle></DialogHeader>
                  <UserList items={followers} empty="Aucun follower" onPick={(id) => { setShowFollowers(false); navigate(`/profile/${id}`); }} />
                </DialogContent>
              </Dialog>
              <Dialog open={showFollowing} onOpenChange={setShowFollowing}>
                <DialogTrigger asChild>
                  <button className="text-center py-2 rounded-xl hover:bg-card transition-colors" onClick={fetchFollowing} data-testid="following-count-btn">
                    <div className="font-display text-xl font-bold">{profileUser?.following_count || 0}</div><div className="text-[11px] text-muted-foreground">Abonnements</div>
                  </button>
                </DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Abonnements</DialogTitle></DialogHeader>
                  <UserList items={following} empty="Aucun abonnement" onPick={(id) => { setShowFollowing(false); navigate(`/profile/${id}`); }} />
                </DialogContent>
              </Dialog>
              <div className="text-center py-2"><div className="font-display text-xl font-bold text-primary">{likesTotal}</div><div className="text-[11px] text-muted-foreground">Likes reçus</div></div>
            </div>
          </div>
        </section>

        <section className="mt-8">
          <SectionTitle eyebrow="Activité">Publications</SectionTitle>
          {posts.length === 0 ? (
            <EmptyState emoji="📝" title="Aucune publication" description={isOwnProfile ? 'Partagez une alerte, un événement ou un bon plan avec votre commune.' : "Cet utilisateur n'a encore rien publié."} testId="profile-no-posts" />
          ) : (
            <div className="space-y-4 stagger-children">
              {posts.map((post) => (
                <div key={post.post_id} data-testid={`profile-post-${post.post_id}`}>
                  <PostCard post={post} api={api} onUpdate={updatePost} compact />
                </div>
              ))}
            </div>
          )}
        </section>
      </PageContent>
    </Page>
  );
};

export default ProfilePage;
